import { Banner, Button, Tooltip, Typography, toast } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import { addNotification, consoleApi, getApiError, getErrorMessage, useActiveOrg } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { Page, PageTitle } from '@nx/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useState } from 'react';

import { ConfirmMfaModal } from '../../account/preferences/confirm-mfa-modal';

const logger = createLogger(APP_SCOPE.framework);

export const Mfa = () => {
  const activeOrg = useActiveOrg();
  const [updateOrgAppMfaRequired, updateOrgAppMfaRequiredRes] = consoleApi.useUpdateOrgAppMfaRequiredMutation();
  const [showConfirmMfaRequiredModal, setShowConfirmMfaRequiredModal] = useState<boolean>(false);
  const [mfaRequired, setMfaRequired] = useState(activeOrg.appMfaRequired);

  const handleUpdateAppMfaRequired = () => {
    const { appMfaRequired } = activeOrg;
    updateOrgAppMfaRequired({ organizationId: activeOrg.id, appMfaRequired: !appMfaRequired })
      .unwrap()
      .then(() => {
        toast.success(`Successfully ${appMfaRequired ? 'disabled' : 'enabled'} MFA.`, { shouldAutoClose: true });
        setShowConfirmMfaRequiredModal(false);
        setMfaRequired(!appMfaRequired);
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        logger.error(error.message);
        addNotification({
          type: 'danger',
          title: `Failed to ${appMfaRequired ? 'disable' : 'enable'} MFA`,
          description: getErrorMessage(error),
          timeout: 5000,
        });
      });
  };

  const onToggleMfaChange = () => {
    setShowConfirmMfaRequiredModal(true);
  };

  return (
    <Page>
      <PageTitle title="MFA (Multi-Factor Authentication)" back={{ label: 'Back to security', toUrl: '../security' }} />
      <Banner
        type={mfaRequired ? 'warning' : 'info'}
        usage="inline"
        title={
          mfaRequired ? 'Disable MFA' : 'Enabling MFA will require all users to re-authenticate for this organization'
        }
        hasIcon={true}
      >
        <div className="flex flex-col gap-4">
          <Typography variant="body-medium">
            {mfaRequired
              ? 'Neo4j does not recommend turning off MFA because it will significantly reduce your account security'
              : 'Set up the second form of verification before logging in again'}
          </Typography>
          <Tooltip type="simple" isDisabled={isNullish(activeOrg.ssoOrgId) || activeOrg.ssoOrgId === ''}>
            <Tooltip.Trigger hasButtonWrapper>
              <Button
                size="small"
                color="neutral"
                fill="outlined"
                onClick={onToggleMfaChange}
                className="w-fit"
                isDisabled={!(isNullish(activeOrg.ssoOrgId) || activeOrg.ssoOrgId === '')}
              >
                {mfaRequired ? 'Disable' : 'Enable'} MFA
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content style={{ maxWidth: 200 }}>
              MFA is required through your SSO Identity Provider (IdP).
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Banner>
      {showConfirmMfaRequiredModal && (
        <ConfirmMfaModal
          onClose={() => setShowConfirmMfaRequiredModal(false)}
          onConfirm={handleUpdateAppMfaRequired}
          title={
            activeOrg.appMfaRequired
              ? 'Disable organization Multi Factor Authentication (MFA)?'
              : 'Enable organization Multi Factor Authentication (MFA)?'
          }
          isLoading={updateOrgAppMfaRequiredRes.isLoading}
          mfaEnabled={activeOrg.appMfaRequired}
          description=""
        />
      )}
    </Page>
  );
};
